import buildTimeConfig from '../../config/buildtime.config';
import config from '../../config/config';

// sessionStorage key used to hand a partner-supplied phone number (customer has
// no Swift247 account yet) from index.tsx's bootstrap to ValidatePhoneNumber,
// which prefills it into the phone field on mount instead of asking the user
// to type it again.
export const WEBVIEW_BYPASS_PHONE_KEY = 's247_webview_bypass_phone';

interface PartnerThemeConfig {
  primaryColor?: string;
  secondColor?: string;
  gradientBtn?: string;
  gradientHeader?: string;
  borderRadius?: string;
  togglePadding?: string;
  toggleBg?: string;
  toggleInactiveColor?: string;
  toggleActiveBg?: string;
  toggleActiveColor?: string;
  // Optional partner banner carousel shown on the first login screen instead
  // of the "Welcome to Swift247!" title — falls back to the title when empty.
  // Keyed by language so each locale can show its own set of images.
  backgroundImagesUrl?: Partial<Record<'en' | 'vi', string[]>>;
}

// Reads the partner banner image list cached by loadPartnerSettings() for the
// given language. Falls back to 'en' when the requested language has no list,
// and returns [] when unset/malformed so callers can fall back to the default title.
export const getPartnerBannerImages = (lang?: string): string[] => {
  const cachedRaw = localStorage.getItem('s247_partner_theme');
  if (!cachedRaw) return [];
  try {
    const themeConfig: PartnerThemeConfig = JSON.parse(cachedRaw);
    const byLanguage = themeConfig.backgroundImagesUrl;
    if (!byLanguage) return [];
    const normalizedLang = lang === 'vi' ? 'vi' : 'en';
    const images = byLanguage[normalizedLang] ?? byLanguage.en;
    return Array.isArray(images) ? images : [];
  } catch {
    return [];
  }
};

export const getOAuthCredentials = () =>
  ({ clientId: config.clientId, clientSecret: config.clientSecret });

const normalizeValue = (val: string): string =>
  val.trim().replace(/^["']|["']$/g, '').replace(/\r\n|\r|\n/g, ' ').replace(/\s+/g, ' ').trim();

const generateAuthPartnerCss = (cfg: PartnerThemeConfig): string => {
  const p = cfg.primaryColor ? normalizeValue(cfg.primaryColor) : undefined;
  const second = cfg.secondColor ? normalizeValue(cfg.secondColor) : undefined;
  const btn = cfg.gradientBtn ? normalizeValue(cfg.gradientBtn) : p;
  const radius = cfg.borderRadius ? normalizeValue(cfg.borderRadius) : undefined;
  const togglePadding = cfg.togglePadding ? normalizeValue(cfg.togglePadding) : '4px';
  const toggleBg = cfg.toggleBg ? normalizeValue(cfg.toggleBg) : '#F2F2F2';
  const toggleInactiveColor = cfg.toggleInactiveColor ? normalizeValue(cfg.toggleInactiveColor) : '#888888';
  const toggleActiveBg = cfg.toggleActiveBg ? normalizeValue(cfg.toggleActiveBg) : '#FFFFFF';
  const toggleActiveColor = cfg.toggleActiveColor ? normalizeValue(cfg.toggleActiveColor) : '#1A1A1A';

  if (!p) return '';

  const blocks: string[] = [];

  const header = cfg.gradientHeader ? normalizeValue(cfg.gradientHeader) : undefined;
  const vars = [`  --s247-primary: ${p};`, `  --s247-gradient-btn: ${btn ?? p};`];
  if (second) vars.push(`  --s247-second: ${second};`);
  if (header) vars.push(`  --s247-gradient-header: ${header};`);
  if (radius) vars.push(`  --s247-btn-radius: ${radius};`);
  blocks.push(`:root {\n${vars.join('\n')}\n}`);

  blocks.push(`
/* Primary button */
.ant-btn:not(.ant-btn-default):not([disabled]) {
  background: ${btn ?? p} !important;
  border-color: ${btn ?? p} !important;
}
.ant-btn:not(.ant-btn-default):not([disabled]):hover {
  background: ${btn ?? p} !important;
  border-color: ${btn ?? p} !important;
  opacity: 0.9;
}
.ant-btn:not(.ant-btn-default):not([disabled]):focus {
  background: ${btn ?? p} !important;
  border-color: ${btn ?? p} !important;
}

/* Checkbox */
.ant-checkbox-checked .ant-checkbox-inner { background-color: ${p} !important; border-color: ${p} !important; }
.ant-checkbox-checked::after { border-color: ${p} !important; }
.ant-checkbox-input:focus + .ant-checkbox-inner,
.ant-checkbox-input:hover + .ant-checkbox-inner { border-color: ${p} !important; }
input[type='checkbox'] { accent-color: ${p} !important; }

/* Account type radio toggle */
.radio-button + label { border-color: ${p} !important; }
.radio-button:checked + label { background-color: ${p} !important; color: #fff !important; }

/* Ant radio */
.ant-radio.ant-radio-checked .ant-radio-inner { border-color: ${p} !important; }
.ant-radio.ant-radio-checked .ant-radio-inner::after { background-color: ${p} !important; }
.ant-radio:hover .ant-radio-inner { border-color: ${p} !important; }

/* Spinner */
.ant-spin-dot-item { background-color: ${p} !important; }

/* Links */
.forgot-password-btn { color: ${p} !important; }
.term-link { color: ${second ?? p} !important; }

/* Select dropdown */
.ant-select-item-option-selected:not(.ant-select-item-option-disabled) { color: ${p} !important; }
.ant-select-focused .ant-select-selector { color: ${p} !important; }
  `.trim());

  blocks.push(
    `.header-title .title { background: ${btn ?? p} !important; background-clip: text !important; -webkit-background-clip: text !important; -webkit-text-fill-color: transparent !important; }`
  );

  if (radius) {
    blocks.push(`
/* Button border radius */
.ant-btn { border-radius: ${radius} !important; }

/* Radio toggle — pill/segmented style */
.login-container .login-field .account-form .radio-button-group { background-color: ${toggleBg} !important; border-radius: ${radius} !important; padding: ${togglePadding} !important; border: none !important; }
.login-container .login-field .account-form .radio-button-group .item .radio-button + label { border-color: transparent !important; border-radius: ${radius} !important; background-color: transparent !important; color: ${toggleInactiveColor} !important; font-weight: 400 !important; }
.login-container .login-field .account-form .radio-button-group .item .radio-button:checked + label { background-color: ${toggleActiveBg} !important; color: ${toggleActiveColor} !important; border-color: transparent !important; font-weight: 600 !important; box-shadow: 0 1px 3px rgba(0,0,0,0.12) !important; }
    `.trim());
  }

  return blocks.join('\n\n');
};

const injectPartnerCss = (css: string) => {
  const id = 'partner-custom-css';
  document.getElementById(id)?.remove();
  const style = document.createElement('style');
  style.id = id;
  style.textContent = css;
  document.head.appendChild(style);
};

// Redirect to booking portal.
// - With a bypass `token` (partner-initiated login, e.g. Vikki): always forward via
//   booking's existing `?token=&partner=` query-param bootstrap — works the same
//   regardless of origin, so no hash trickery needed here.
// - Without a token (normal post-login flow):
//   Local dev: auth + booking on different ports (different origins) → localStorage
//     not shared → pass theme/partner/cs via URL hash so booking can bootstrap itself.
//   Non-local: same domain → booking portal reads localStorage directly → plain redirect.
export const redirectToBooking = (token?: string): void => {
  const partner = localStorage.getItem('s247_webview_partner');

  if (token) {
    const params = new URLSearchParams();
    params.set('token', token);
    if (partner) params.set('partner', partner);
    window.location = `${config.bookingUrl}?${params.toString()}` as any;
    return;
  }

  if (buildTimeConfig.env !== 'local') {
    window.location = config.bookingUrl as any;
    return;
  }

  const parts: string[] = [];
  const themeRaw = localStorage.getItem('s247_partner_theme');
  if (themeRaw) parts.push(`s247_theme=${encodeURIComponent(themeRaw)}`);
  if (partner) parts.push(`s247_partner=${encodeURIComponent(partner)}`);
  const hash = parts.length ? `#${parts.join('&')}` : '';
  window.location = `${config.bookingUrl}${hash}` as any;
};

export const getWebViewPartnerName = (): string | null => {
  return localStorage.getItem('s247_webview_partner');
};

export const isWebView = (): boolean => {
  return getWebViewPartnerName() !== null || localStorage.getItem('s247_partner_theme') !== null;
};

// Inside the partner webview, target="_blank" links (terms, privacy policy...)
// just navigate the same embedded webview away instead of opening a new one —
// real mobile apps open outside links in their own native browser sheet
// instead. Post the same kind of message the webview back button already
// uses and let the native app decide how to open it (e.g. Linking.openURL
// into the system browser).
export const openExternalLink = (url: string): void => {
  const message = JSON.stringify({ event: 'OPEN_EXTERNAL_LINK', url });
  const rn = (window as any).ReactNativeWebView;
  if (rn) {
    rn.postMessage(message);
  } else {
    window.parent.postMessage(message, '*');
  }
};

export interface WebViewBootstrapResult {
  partner: string | null;
  // Partner already resolved a full Swift247 user token (e.g. via
  // POST /v1/partner/user-token) — forward straight to booking, skip login entirely.
  token: string | null;
  // Partner has no way to resolve a token for this customer (no Swift247 account
  // yet) — land on the normal login screen with this phone pre-filled so the
  // user can review/edit it before submitting, instead of typing it from scratch.
  phone: string | null;
}

// Read ?partner/?token/?phone/?lang from URL query params, clean them immediately
// to keep the URL clean.
export const bootstrapWebView = (): WebViewBootstrapResult => {
  const params = new URLSearchParams(window.location.search);
  const partner = params.get('partner') || null;
  const token = params.get('token') || null;
  const phone = params.get('phone') || null;
  const lang = params.get('lang') || null;

  if (lang) {
    localStorage.setItem('_app_locale_key', lang);
  }

  if (partner || token || phone || lang) {
    params.delete('partner');
    params.delete('token');
    params.delete('phone');
    params.delete('lang');
    const newSearch = params.toString();
    window.history.replaceState(null, '', window.location.pathname + (newSearch ? `?${newSearch}` : ''));
  }

  return { partner, token, phone };
};

// Load partner settings:
// - If partner present: GET /v1/partner/{partner}/settings → store theme in localStorage
// - If no partner (page reload): apply cached theme only
export const loadPartnerSettings = async (partner: string | null): Promise<void> => {
  if (partner) {
    try {
      const res = await fetch(`${config.mainURL}/v1/partner/${partner}/settings`);
      if (!res.ok) return;
      const data = await res.json();

      if (data.theme_config) {
        localStorage.setItem('s247_partner_theme', JSON.stringify(data.theme_config));
        const css = generateAuthPartnerCss(data.theme_config);
        if (css) injectPartnerCss(css);
      }

      if (data.partner_name) {
        localStorage.setItem('s247_webview_partner', data.partner_name);
      }
    } catch {
      // silently fail — default config stays
    }
    return;
  }

  // No partner (reload scenario) — apply cached theme only
  const cachedRaw = localStorage.getItem('s247_partner_theme');
  if (!cachedRaw) return;
  try {
    const themeConfig: PartnerThemeConfig = JSON.parse(cachedRaw);
    const css = generateAuthPartnerCss(themeConfig);
    if (css) injectPartnerCss(css);
  } catch {
    localStorage.removeItem('s247_partner_theme');
  }
};
